import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  AlertTriangle,
  Building2,
  Clock,
  CreditCard,
  Eye,
  FileText,
  Package,
  Receipt,
  RefreshCw,
  Search,
  Truck,
  UserPlus,
  Warehouse,
  X,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_BASE } from '@/lib/api-base';
import {
  type PortalRow,
} from './revochem-service-portal-fixture';

type WorkspaceConfig = {
  title: string;
  description: string;
  icon: typeof Truck;
  tabs: string[];
  statusTabs: string[];
  rows: PortalRow[];
  renderActions?: (activeObject: string) => ReactNode;
  onRowClick?: (row: PortalRow) => void;
  getRowActionLabel?: (row: PortalRow, activeObject: string) => string | null;
};

type ServicePortalData = {
  source: 'fixture' | 'live';
  overviewStats: Array<{ label: string; value: number }>;
  rows: {
    trucking: PortalRow[];
    depot: PortalRow[];
    warehouse: PortalRow[];
    bills: PortalRow[];
    documents: PortalRow[];
    urgent: PortalRow[];
  };
};

type ContainerLifecycle = {
  container: {
    id: string;
    visitRef: string;
    containerNo: string;
    sizeType: string;
    lineOperator: string;
    status: string;
    condition: string;
    hold: string;
    slot: string;
    gateInAt: string;
    gateOutAt: string;
    nextAction: string;
  };
  summary: {
    lifecycleCompletion: number;
    totalCost: number;
    totalSell: number;
    margin: number;
    chargeCount: number;
    documentCount: number;
    financeActionCount: number;
  };
  timeline: Array<{
    stage: string;
    status: string;
    label: string;
    detail: string;
    timestamp: string;
    evidenceRef?: string;
  }>;
  costing: Array<{
    activity: string;
    ref: string;
    status: string;
    costingPrice: number;
    costingSource: string;
    listPrice: number;
    pricingSource: string;
    invoiceRef: string;
    billingStatus: string;
    disputeStatus: string;
    occurredAt: string;
  }>;
  repair: Array<{ ref: string; status: string; amount: number; currency: string; summary: string; updated: string }>;
  documents: Array<{ ref: string; type: string; status: string; label: string; updated: string }>;
  financeActions: PortalRow[];
};

const emptyPortalData: ServicePortalData = {
  source: 'live',
  overviewStats: [
    { label: 'Needs Action', value: 0 },
    { label: 'Today / Upcoming', value: 0 },
    { label: 'Documents Ready', value: 0 },
    { label: 'Payment Notices', value: 0 },
  ],
  rows: {
    trucking: [],
    depot: [],
    warehouse: [],
    bills: [],
    documents: [],
    urgent: [],
  },
};

function useServicePortalData() {
  const { accessToken } = useAuth();
  const [data, setData] = useState<ServicePortalData>(emptyPortalData);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/selfservice/service-portal`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: AbortSignal.timeout(8000),
        });
        const envelope = await res.json() as { ok: boolean; data?: ServicePortalData };
        if (!res.ok || !envelope.ok || !envelope.data?.rows) throw new Error('Portal data unavailable');
        if (!cancelled) setData({ ...envelope.data, source: 'live' });
      } catch {
        if (!cancelled) setData(emptyPortalData);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [accessToken]);

  return data;
}

const badgeClass: Record<string, string> = {
  Active: 'bg-blue-50 text-blue-700',
  Submitted: 'bg-sky-50 text-sky-700',
  Confirmed: 'bg-emerald-50 text-emerald-700',
  Blocked: 'bg-red-50 text-red-700',
  Exception: 'bg-amber-50 text-amber-700',
  Completed: 'bg-slate-100 text-slate-600',
  Pending: 'bg-amber-50 text-amber-700',
  Approved: 'bg-emerald-50 text-emerald-700',
  Open: 'bg-blue-50 text-blue-700',
  Overdue: 'bg-red-50 text-red-700',
  Paid: 'bg-emerald-50 text-emerald-700',
  Disputed: 'bg-orange-50 text-orange-700',
};

function money(value: number, currency = 'SGD') {
  return `${currency} ${Number(value || 0).toFixed(2)}`;
}

function filterRows(rows: PortalRow[], activeObject: string, activeStatus: string, search: string) {
  const q = search.trim().toLowerCase();
  return rows.filter(row => {
    const objectMatch = activeObject === 'All' || row.service === activeObject;
    const statusMatch = activeStatus === 'All' || row.status === activeStatus;
    const searchMatch =
      !q ||
      row.ref.toLowerCase().includes(q) ||
      row.subject.toLowerCase().includes(q) ||
      row.nextAction.toLowerCase().includes(q);
    return objectMatch && statusMatch && searchMatch;
  });
}

function PortalWorkspacePage({ config }: { config: WorkspaceConfig }) {
  const [activeObject, setActiveObject] = useState(config.tabs[0] ?? 'All');
  const [activeStatus, setActiveStatus] = useState('All');
  const [search, setSearch] = useState('');
  const Icon = config.icon;

  const visibleRows = useMemo(
    () => filterRows(config.rows, activeObject, activeStatus, search),
    [activeObject, activeStatus, config.rows, search],
  );

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Icon className="w-5 h-5 text-brand-700" />
            {config.title}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{config.description}</p>
        </div>
        {config.title === 'Trucking' && (
          <button className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800">
            <UserPlus className="w-4 h-4" />
            Create Booking
          </button>
        )}
        {config.renderActions?.(activeObject)}
      </div>

      <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {config.tabs.map(tab => (
            <button
              key={tab}
              onClick={() => {
                setActiveObject(tab);
                setActiveStatus('All');
              }}
              className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium ${
                activeObject === tab
                  ? 'bg-brand-700 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative min-w-64 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search reference, container, order, action..."
            className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-4 text-sm focus:border-brand-400 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto">
          {config.statusTabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveStatus(tab)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${
                activeStatus === tab
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50">
            <tr>
              {[
                'Reference',
                'Subject',
                'Status',
                'Next Action',
                'Updated',
                ...(config.getRowActionLabel ? ['Action'] : []),
              ].map(column => (
                <th key={column} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {visibleRows.map(row => {
              const actionLabel = config.getRowActionLabel?.(row, activeObject);
              return (
              <tr key={row.ref} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-brand-700">{row.ref}</td>
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-900">{row.subject}</p>
                  <p className="text-xs text-slate-400">{row.service}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass[row.status] ?? 'bg-slate-100 text-slate-600'}`}>
                    {row.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">{row.nextAction}</td>
                <td className="px-4 py-3 whitespace-nowrap text-slate-400">{row.updated}</td>
                {config.getRowActionLabel && (
                  <td className="px-4 py-3">
                    {actionLabel ? (
                      <button
                        onClick={() => config.onRowClick?.(row)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-brand-300 hover:text-brand-700"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        {actionLabel}
                      </button>
                    ) : null}
                  </td>
                )}
              </tr>
            );})}
          </tbody>
        </table>
        {visibleRows.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-slate-400">
            No rows match this view.
          </div>
        )}
      </div>
    </div>
  );
}

function ContainerLifecycleDrawer({ row, onClose }: { row: PortalRow | null; onClose: () => void }) {
  const { accessToken } = useAuth();
  const [data, setData] = useState<ContainerLifecycle | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'costing' | 'repair' | 'documents' | 'activity'>('costing');

  useEffect(() => {
    if (!row?.entityId || !accessToken) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/selfservice/service-portal/depot/containers/${row.entityId}/lifecycle`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal: AbortSignal.timeout(8000),
        });
        const envelope = await res.json() as { ok: boolean; data?: ContainerLifecycle };
        if (!res.ok || !envelope.ok || !envelope.data) throw new Error('Container lifecycle unavailable');
        if (!cancelled) setData(envelope.data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [accessToken, row?.entityId]);

  if (!row) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30">
      <div className="h-full w-full max-w-5xl overflow-y-auto bg-white shadow-2xl">
        <div className="sticky top-0 z-10 border-b border-slate-100 bg-white px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Container Lifecycle</p>
              <h2 className="mt-1 text-lg font-bold text-slate-900">{data?.container.containerNo ?? row.ref}</h2>
              <p className="mt-1 text-sm text-slate-500">{data?.container.nextAction ?? row.nextAction}</p>
            </div>
            <button onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-900">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {loading && (
          <div className="px-5 py-10 text-sm text-slate-500">Loading lifecycle...</div>
        )}

        {!loading && !data && (
          <div className="px-5 py-10 text-sm text-slate-500">
            Lifecycle detail is not available for this row yet.
          </div>
        )}

        {data && (
          <div className="space-y-5 px-5 py-5">
            <div className="grid gap-3 md:grid-cols-4">
              {[
                ['Status', data.container.status],
                ['Yard Slot', data.container.slot],
                ['Completion', `${data.summary.lifecycleCompletion}%`],
                ['Margin', money(data.summary.margin)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">{value}</p>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-slate-100">
              <div className="border-b border-slate-100 px-4 py-3">
                <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <Clock className="h-4 w-4 text-brand-700" />
                  Journey Timeline
                </h3>
              </div>
              <div className="divide-y divide-slate-50">
                {data.timeline.map((event, index) => (
                  <div key={`${event.stage}-${event.evidenceRef ?? index}`} className="grid gap-3 px-4 py-3 text-sm md:grid-cols-[140px_120px_1fr_140px]">
                    <span className="font-semibold text-slate-900">{event.stage}</span>
                    <span className={`w-fit rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass[event.status] ?? 'bg-slate-100 text-slate-600'}`}>
                      {event.status}
                    </span>
                    <div>
                      <p className="font-medium text-slate-800">{event.label}</p>
                      <p className="text-xs text-slate-500">{event.detail}</p>
                    </div>
                    <span className="text-xs text-slate-400">{event.timestamp}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 overflow-x-auto">
              {[
                ['costing', 'Costing'],
                ['repair', 'Repair'],
                ['documents', 'Documents'],
                ['activity', 'Finance Actions'],
              ].map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key as typeof activeTab)}
                  className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold ${
                    activeTab === key ? 'bg-slate-900 text-white' : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeTab === 'costing' && (
              <div className="overflow-hidden rounded-xl border border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      {['Activity', 'Cost / Source', 'Price / Source', 'Invoice', 'Status'].map(column => (
                        <th key={column} className="px-3 py-2 text-left font-semibold">{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {data.costing.map(line => (
                      <tr key={line.ref}>
                        <td className="px-3 py-2">
                          <p className="font-semibold text-slate-900">{line.activity}</p>
                          <p className="text-xs text-slate-400">{line.ref} · {line.occurredAt}</p>
                        </td>
                        <td className="px-3 py-2">
                          <p className="font-medium text-slate-800">{money(line.costingPrice)}</p>
                          <p className="text-xs text-slate-400">{line.costingSource}</p>
                        </td>
                        <td className="px-3 py-2">
                          <p className="font-medium text-slate-800">{money(line.listPrice)}</p>
                          <p className="text-xs text-slate-400">{line.pricingSource}</p>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{line.invoiceRef}</td>
                        <td className="px-3 py-2">
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass[line.disputeStatus] ?? 'bg-slate-100 text-slate-600'}`}>
                            {line.disputeStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.costing.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">No charge activity yet.</div>}
              </div>
            )}

            {activeTab === 'repair' && (
              <div className="overflow-hidden rounded-xl border border-slate-100">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-50">
                    {data.repair.map(item => (
                      <tr key={item.ref}>
                        <td className="px-3 py-2 font-semibold text-brand-700">{item.ref}</td>
                        <td className="px-3 py-2">{item.summary}</td>
                        <td className="px-3 py-2">{item.currency} {item.amount.toFixed(2)}</td>
                        <td className="px-3 py-2">{item.status}</td>
                        <td className="px-3 py-2 text-slate-400">{item.updated}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.repair.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">No repair estimate for this container.</div>}
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="overflow-hidden rounded-xl border border-slate-100">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-50">
                    {data.documents.map(item => (
                      <tr key={item.ref}>
                        <td className="px-3 py-2 font-semibold text-brand-700">{item.ref}</td>
                        <td className="px-3 py-2">{item.type}</td>
                        <td className="px-3 py-2">{item.label}</td>
                        <td className="px-3 py-2">{item.status}</td>
                        <td className="px-3 py-2 text-slate-400">{item.updated}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.documents.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">No lifecycle documents linked yet.</div>}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="overflow-hidden rounded-xl border border-slate-100">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-50">
                    {data.financeActions.map(item => (
                      <tr key={item.ref}>
                        <td className="px-3 py-2 font-semibold text-brand-700">{item.ref}</td>
                        <td className="px-3 py-2">{item.subject}</td>
                        <td className="px-3 py-2">{item.status}</td>
                        <td className="px-3 py-2 text-slate-400">{item.updated}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.financeActions.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">No payment notice or dispute linked to this container.</div>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function TruckingPage() {
  const portalData = useServicePortalData();
  return (
    <PortalWorkspacePage
      config={{
        title: 'Trucking',
        description: 'Bookings, containers, delivery status, and POD for trucking services.',
        icon: Truck,
        tabs: ['Bookings', 'Containers', 'Documents'],
        statusTabs: ['All', 'Draft', 'Submitted', 'Confirmed', 'Active', 'Exception', 'Completed'],
        rows: portalData.rows.trucking,
      }}
    />
  );
}

export function DepotCustomerPage() {
  const portalData = useServicePortalData();
  const [selectedContainer, setSelectedContainer] = useState<PortalRow | null>(null);
  return (
    <>
      <PortalWorkspacePage
        config={{
          title: 'Depot',
          description: 'Depot bookings, container position, repair approvals, and release blockers.',
          icon: Building2,
          tabs: ['Bookings', 'Containers', 'Repairs'],
          statusTabs: ['All', 'Pending', 'Approved', 'Active', 'Blocked', 'In Progress', 'Completed', 'Disputed'],
          rows: portalData.rows.depot,
          onRowClick: row => setSelectedContainer(row),
          getRowActionLabel: (row, activeObject) => (
            activeObject === 'Containers' && row.entityType === 'depot_container_visit' && row.entityId
              ? 'Lifecycle'
              : null
          ),
        }}
      />
      <ContainerLifecycleDrawer row={selectedContainer} onClose={() => setSelectedContainer(null)} />
    </>
  );
}

export function WarehouseCustomerPage() {
  const portalData = useServicePortalData();
  return (
    <PortalWorkspacePage
      config={{
        title: 'Warehouse',
        description: 'Inventory, inbound/outbound orders, movements, and warehouse exceptions.',
        icon: Warehouse,
        tabs: ['Inventory', 'Orders', 'Movements'],
        statusTabs: ['All', 'Active', 'Submitted', 'Confirmed', 'Exception', 'Completed'],
        rows: portalData.rows.warehouse,
      }}
    />
  );
}

export function BillsPaymentsPage() {
  const { accessToken } = useAuth();
  const portalData = useServicePortalData();
  const [formMode, setFormMode] = useState<'payment' | 'dispute' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    referenceNo: '',
    amount: '',
    currency: 'SGD',
    paymentDate: new Date().toISOString().slice(0, 10),
    targetRefs: '',
    receiptUrl: '',
    remarks: '',
  });
  const [disputeForm, setDisputeForm] = useState({
    targetRef: '',
    targetType: 'invoice',
    amount: '',
    currency: 'SGD',
    evidenceUrl: '',
    reason: '',
  });

  const submitPaymentNotice = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken) return;
    const amount = Number(paymentForm.amount);
    if (!paymentForm.referenceNo.trim() || !Number.isFinite(amount) || amount <= 0) {
      setMessage('Payment Notice needs payment reference and valid amount.');
      return;
    }
    setSubmitting(true);
    setMessage(null);
    const res = await fetch(`${API_BASE}/api/selfservice/payment-notices`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({
        amount,
        currency: paymentForm.currency.toUpperCase(),
        paymentDate: paymentForm.paymentDate,
        referenceNo: paymentForm.referenceNo.trim(),
        targetRefs: paymentForm.targetRefs.split(',').map(item => item.trim()).filter(Boolean),
        receiptUrl: paymentForm.receiptUrl.trim() || undefined,
        remarks: paymentForm.remarks.trim() || undefined,
      }),
    });
    setSubmitting(false);
    if (!res.ok) {
      setMessage('Payment Notice submission failed. Check billing access and customer scope.');
      return;
    }
    setMessage('Payment Notice submitted for tenant finance validation.');
    setFormMode(null);
  };

  const submitDispute = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken) return;
    const amount = disputeForm.amount ? Number(disputeForm.amount) : undefined;
    if (!disputeForm.targetRef.trim() || disputeForm.reason.trim().length < 5) {
      setMessage('Dispute needs target reference and clear reason.');
      return;
    }
    setSubmitting(true);
    setMessage(null);
    const res = await fetch(`${API_BASE}/api/selfservice/billing-disputes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({
        targetRef: disputeForm.targetRef.trim(),
        targetType: disputeForm.targetType,
        reason: disputeForm.reason.trim(),
        amount: Number.isFinite(amount) ? amount : undefined,
        currency: disputeForm.currency.toUpperCase(),
        evidenceUrl: disputeForm.evidenceUrl.trim() || undefined,
      }),
    });
    setSubmitting(false);
    if (!res.ok) {
      setMessage('Dispute submission failed. Check billing access and customer scope.');
      return;
    }
    setMessage('Billing dispute submitted for tenant finance review.');
    setFormMode(null);
  };

  return (
    <div className="space-y-4">
      {message && (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
          {message}
        </div>
      )}
      <PortalWorkspacePage
        config={{
          title: 'Bills & Payments',
          description: 'Invoices, SOA, Payment Notice, and billing disputes across enabled services.',
          icon: Receipt,
          tabs: ['Invoices & SOA', 'Payment Notice', 'Disputes'],
          statusTabs: ['All', 'Open', 'Overdue', 'Paid', 'Pending', 'Disputed', 'Completed'],
          rows: portalData.rows.bills,
          renderActions: activeObject => {
            if (activeObject === 'Payment Notice') {
              return (
                <button onClick={() => setFormMode('payment')} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800">
                  <CreditCard className="w-4 h-4" />
                  Submit Payment Notice
                </button>
              );
            }
            if (activeObject === 'Disputes') {
              return (
                <button onClick={() => setFormMode('dispute')} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">
                  <AlertTriangle className="w-4 h-4" />
                  Raise Dispute
                </button>
              );
            }
            return null;
          },
        }}
      />

      {formMode === 'payment' && (
        <form onSubmit={submitPaymentNotice} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
          <div className="grid gap-3 md:grid-cols-5">
            <input value={paymentForm.referenceNo} onChange={event => setPaymentForm(prev => ({ ...prev, referenceNo: event.target.value }))} placeholder="Payment ref" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input value={paymentForm.amount} onChange={event => setPaymentForm(prev => ({ ...prev, amount: event.target.value }))} placeholder="Amount" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input value={paymentForm.currency} onChange={event => setPaymentForm(prev => ({ ...prev, currency: event.target.value }))} placeholder="Currency" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input type="date" value={paymentForm.paymentDate} onChange={event => setPaymentForm(prev => ({ ...prev, paymentDate: event.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input value={paymentForm.targetRefs} onChange={event => setPaymentForm(prev => ({ ...prev, targetRefs: event.target.value }))} placeholder="Invoice refs, comma separated" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <input value={paymentForm.receiptUrl} onChange={event => setPaymentForm(prev => ({ ...prev, receiptUrl: event.target.value }))} placeholder="Receipt / bank proof link" className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <textarea value={paymentForm.remarks} onChange={event => setPaymentForm(prev => ({ ...prev, remarks: event.target.value }))} placeholder="Remarks / bank receipt note" className="mt-3 min-h-20 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <div className="mt-3 flex gap-2">
            <button disabled={submitting} className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Submit for Validation</button>
            <button type="button" onClick={() => setFormMode(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">Cancel</button>
          </div>
        </form>
      )}

      {formMode === 'dispute' && (
        <form onSubmit={submitDispute} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
          <div className="grid gap-3 md:grid-cols-4">
            <input value={disputeForm.targetRef} onChange={event => setDisputeForm(prev => ({ ...prev, targetRef: event.target.value }))} placeholder="Invoice / SOA / charge ref" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <select value={disputeForm.targetType} onChange={event => setDisputeForm(prev => ({ ...prev, targetType: event.target.value }))} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="invoice">Invoice</option>
              <option value="soa">SOA</option>
              <option value="charge">Charge</option>
              <option value="payment_notice">Payment Notice</option>
              <option value="other">Other</option>
            </select>
            <input value={disputeForm.amount} onChange={event => setDisputeForm(prev => ({ ...prev, amount: event.target.value }))} placeholder="Disputed amount" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input value={disputeForm.currency} onChange={event => setDisputeForm(prev => ({ ...prev, currency: event.target.value }))} placeholder="Currency" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <input value={disputeForm.evidenceUrl} onChange={event => setDisputeForm(prev => ({ ...prev, evidenceUrl: event.target.value }))} placeholder="Evidence link" className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <textarea value={disputeForm.reason} onChange={event => setDisputeForm(prev => ({ ...prev, reason: event.target.value }))} placeholder="Reason and expected correction" className="mt-3 min-h-24 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <div className="mt-3 flex gap-2">
            <button disabled={submitting} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Submit Dispute</button>
            <button type="button" onClick={() => setFormMode(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

export function CustomerUsersPage() {
  const { user } = useAuth();
  const rows: PortalRow[] = [
    { ref: user?.email ?? 'admin@revochem.example', subject: user?.name ?? 'Revochem Portal Admin', service: 'Active', status: 'Active', nextAction: 'Manage role and service access', updated: 'Current session' },
    { ref: 'ops@revochem.example', subject: 'Operations user', service: 'Invited', status: 'Pending', nextAction: 'Resend invite', updated: 'Today 09:00' },
    { ref: 'finance@revochem.example', subject: 'Finance user', service: 'Active', status: 'Active', nextAction: 'Reset password', updated: 'Yesterday' },
    { ref: 'former.user@revochem.example', subject: 'Former operator', service: 'Deactivated', status: 'Completed', nextAction: 'Hidden from login', updated: 'Last week' },
  ];

  return (
    <PortalWorkspacePage
      config={{
        title: 'Users',
        description: 'Manage customer login users. Deactivated users are hidden but retained for audit.',
        icon: UserPlus,
        tabs: ['All', 'Active', 'Invited', 'Locked', 'Deactivated'],
        statusTabs: ['All', 'Active', 'Pending', 'Completed'],
        rows,
      }}
    />
  );
}

export function DocumentsReportsPage() {
  const portalData = useServicePortalData();
  return (
    <PortalWorkspacePage
      config={{
        title: 'Documents & Reports',
        description: 'Cross-service documents, reports, scheduled delivery, and download history.',
        icon: FileText,
        tabs: ['Documents', 'Reports', 'Download History'],
        statusTabs: ['All', 'Active', 'Completed', 'Exception'],
        rows: portalData.rows.documents,
      }}
    />
  );
}

export function OverviewPage() {
  const { user } = useAuth();
  const portalData = useServicePortalData();
  const statIconByLabel = {
    'Needs Action': AlertTriangle,
    'Today / Upcoming': Package,
    'Documents Ready': FileText,
    'Payment Notices': CreditCard,
  };

  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Overview</h1>
        <p className="mt-1 text-sm text-slate-500">
          {user?.customerName ?? 'Customer'} workspace: urgent actions, upcoming work, and recent updates.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {portalData.overviewStats.map(item => {
          const Icon = statIconByLabel[item.label as keyof typeof statIconByLabel] ?? Package;
          return (
          <div key={item.label} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
            <Icon className="h-5 w-5 text-brand-700" />
            <p className="mt-3 text-2xl font-bold text-slate-900">{item.value}</p>
            <p className="text-xs text-slate-500">{item.label}</p>
          </div>
          );
        })}
      </div>

      <PortalWorkspacePage
        config={{
          title: 'Needs Action',
          description: 'Approvals, blocked items, missing information, payment holds, and booking confirmations.',
          icon: RefreshCw,
          tabs: ['Needs Action', 'Today / Upcoming', 'Recent Updates'],
          statusTabs: ['All', 'Pending', 'Submitted', 'Blocked', 'Completed'],
          rows: portalData.rows.urgent,
        }}
      />
    </div>
  );
}
